import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export interface Campaign {
  id: string
  headline: string
  description: string
  ctaText: string
  ctaLink: string
  imageUrl: string
  backgroundColor?: string
}

interface HeroSliderProps {
  campaigns: Campaign[]
  autoPlayInterval?: number
}

export default function HeroSlider({ campaigns, autoPlayInterval = 4000 }: HeroSliderProps) {
  const navigate = useNavigate()
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (campaigns.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % campaigns.length)
    }, autoPlayInterval)

    return () => clearInterval(interval)
  }, [currentIndex, campaigns.length, autoPlayInterval])

  const handleDotClick = (index: number) => {
    setCurrentIndex(index)
  }

  const handleCTAClick = (link: string) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener,noreferrer')
    } else {
      navigate(link)
    }
  }

  if (!campaigns || campaigns.length === 0) {
    return null
  }

  return (
    <div className="relative w-full overflow-hidden rounded-2xl mb-12 bg-white shadow-xl border border-gray-200/80 ring-1 ring-gray-100">
      {/* Slider Container with neutral background */}
      <div className="relative min-h-[320px] md:min-h-[380px] bg-gradient-to-br from-gray-50/50 via-white to-gray-50/50">
        {/* Decorative background elements with green accent */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute -left-20 -bottom-20 w-60 h-60 bg-brand-primary/5 rounded-full blur-3xl"></div>
        </div>

        {/* Campaign Content — all slides stacked in one grid cell so the
            container always sizes to the tallest slide (no layout shift) and
            we can crossfade opacity between them (no blank gap). */}
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="grid">
            {campaigns.map((campaign, index) => {
              const isActive = index === currentIndex
              return (
                <div
                  key={campaign.id}
                  aria-hidden={!isActive}
                  style={{ gridArea: '1 / 1' }}
                  className={`flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-12 items-center transition-opacity duration-500 ease-in-out ${
                    isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {/* Product Image - Shows first on mobile, second on desktop */}
                  <div className="w-full md:order-2">
                    <div className="relative h-[200px] md:h-[280px] rounded-xl md:rounded-2xl overflow-hidden group shadow-lg md:shadow-xl">
                      {/* Image with green overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/20 to-transparent z-10"></div>
                      <img
                        src={campaign.imageUrl}
                        alt={campaign.headline}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = 'https://via.placeholder.com/600x600?text=Image+Not+Found'
                        }}
                      />
                      {/* Subtle green border */}
                      <div className="absolute inset-0 rounded-xl md:rounded-2xl ring-1 ring-brand-primary/10"></div>
                    </div>
                  </div>

                  {/* Text Content - Shows second on mobile, first on desktop */}
                  <div className="space-y-4 md:space-y-5 md:order-1">
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
                      {campaign.headline}
                    </h1>
                    <p className="text-sm md:text-lg text-gray-600 leading-relaxed line-clamp-3 font-light">
                      {campaign.description}
                    </p>
                    <div className="pt-1 md:pt-2">
                      <button
                        onClick={() => handleCTAClick(campaign.ctaLink)}
                        tabIndex={isActive ? 0 : -1}
                        className="group relative bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-6 py-3 md:px-8 md:py-4 text-sm md:text-base rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
                      >
                        <span className="relative z-10">{campaign.ctaText}</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/80 to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Navigation Dots - Bottom center with green accent */}
        {campaigns.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 z-20">
            {campaigns.map((_, index) => {
              const isActive = index === currentIndex
              return (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className="rounded-full hover:opacity-80"
                  style={{
                    width: isActive ? '32px' : '10px',
                    height: '10px',
                    backgroundColor: isActive ? '#0B4D2A' : '#D1D5DB',
                    transition: 'all 350ms ease-in-out',
                    boxShadow: isActive ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  aria-label={`Go to campaign ${index + 1}`}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
